import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  OPOSICIONES_BASIC_PLAN,
  getOposicionesModuleFromPath,
  getOposicionRouteForBody,
  isBasicOposicionesModuleAllowed,
  isMultiOposicionPlan,
} from '@/lib/oposicionesAccess';


/**
 * Mapa plan → ruta raíz de su área dedicada.
 * Para añadir un nuevo cuerpo basta con sumar una entrada aquí + la ruta en App.tsx.
 */
const PLAN_ROUTES: Record<string, string> = {
  [OPOSICIONES_BASIC_PLAN]: '/app/oposiciones',
  base_comun: '/app/base-comun',
  oposiciones: '/app/oposiciones',
  oposiciones_c1: '/app/oposiciones-c1',
  oposiciones_iipp: '/app/oposiciones-iipp',
  oposiciones_guardia_civil: '/app/guardia-civil',
  oposiciones_c2: '/app/oposiciones-c2',
  oposiciones_policia_nacional: '/app/policia-nacional',
  'policia-nacional': '/app/policia-nacional',
  'multi-oposicion': '/app/oposiciones',
  multi_oposicion: '/app/oposiciones',
};

const ALL_OPOS_PREFIXES = [
  '/app/oposiciones',
  '/app/oposiciones-c1',
  '/app/oposiciones-c2',
  '/app/oposiciones-iipp',
  '/app/guardia-civil',
  '/app/policia-nacional',
  '/app/base-comun',
];

function resolveOposBasePath(currentPath: string): string | null {
  return ALL_OPOS_PREFIXES.find((prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`)) ?? null;
}
export function PlanRedirect() {
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!profile) return;
    const currentPath = location.pathname;
    const isPrivileged = profile.is_betatester || profile.role === 'admin' || profile.role === 'super_admin';

    if (profile.is_betatester) return; // betatesters navegan libremente
    // Admins y super_admins tienen acceso total a todas las áreas (funcionarios + oposiciones)
    if (profile.role === 'admin' || profile.role === 'super_admin') return;

    const plan = profile.plan || 'free';
    const planRoute = plan === OPOSICIONES_BASIC_PLAN
      ? getOposicionRouteForBody(profile.opositor_body)
      : PLAN_ROUTES[plan];
    const currentOposBase = resolveOposBasePath(currentPath);

    if (plan === OPOSICIONES_BASIC_PLAN) {
      const selectedOposBase = getOposicionRouteForBody(profile.opositor_body);
      if (currentOposBase && currentOposBase !== selectedOposBase) {
        navigate(`${selectedOposBase}/chat`, { replace: true });
        return;
      }
    }

    if (plan === OPOSICIONES_BASIC_PLAN && currentOposBase) {
      const moduleId = getOposicionesModuleFromPath(currentPath, currentOposBase);
      if (!isBasicOposicionesModuleAllowed(moduleId)) {
        navigate(`${currentOposBase}/progreso?upgrade=academy-completo`, { replace: true });
        return;
      }
    }

    // 1. Plan de oposición pero está en /app/chat (área funcionarios)
    if (planRoute && currentPath.startsWith('/app/chat')) {
      const chatId = currentPath.split('/').pop();
      const target = chatId && chatId !== 'chat' ? `${planRoute}/chat/${chatId}` : `${planRoute}/chat`;
      navigate(target, { replace: true });
      return;
    }

    // 2. Plan free/pago pero está en cualquier área de opositores
    if ((plan === 'free' || plan === 'pago') &&
        ALL_OPOS_PREFIXES.some(prefix => currentPath.startsWith(prefix))) {
      const chatId = currentPath.split('/').pop();
      const target = chatId && chatId !== 'chat' ? `/app/chat/${chatId}` : '/app/chat';
      navigate(target, { replace: true });
    }

    if (planRoute && !isMultiOposicionPlan(plan) && plan !== OPOSICIONES_BASIC_PLAN && currentOposBase && currentOposBase !== planRoute) {
      const moduleId = getOposicionesModuleFromPath(currentPath, currentOposBase);
      navigate(`${planRoute}/${moduleId || 'chat'}`, { replace: true });
    }
  }, [profile, location.pathname, navigate]);

  return null;
}
